import HTTP from '@/services/http'

export default {
  namespaced: true,

  state: {
    allowlist: [],
    blocklist: [],
    hasPrevious: false,
    hasNext: false,
    list: [],
    listAdvertisers: [],
    totalPages: 0,
  },

  mutations: {
    RESET_STATE(state) {
      state.list = []
    },
    SET_HAS_PREVIOUS(state, hasPrevious) {
      state.hasPrevious = hasPrevious
    },
    SET_HAS_NEXT(state, hasNext) {
      state.hasNext = hasNext
    },
    SET_LIST(state, list) {
      state.list = list
    },
    SET_LIST_ADVERTISERS(state, listAdvertisers) {
      state.listAdvertisers = listAdvertisers
    },
    SET_LIST_TOTAL_PAGES(state, totalPages) {
      state.totalPages = totalPages
    },
    SET_BLOCKLIST(state, blocklist) {
      state.blocklist = blocklist
    },
    SET_ALLOWLIST(state, allowlist) {
      state.allowlist = allowlist
    },
  },

  actions: {
    resetState({ commit }) {
      commit('RESET_STATE')
    },

    async toggle({ commit, state, rootState }, { creativeIds, status }) {
      try {
        const { organizationId } = rootState.auth.user

        const toggledCreatives = []

        for (const creativeId of creativeIds) {
          const response = await HTTP.put(
            `Creative/CreativeLibrary/${organizationId}/curation/${creativeId}`,
            { status }
          )

          if (response?.status === 200) {
            toggledCreatives.push(creativeId)
          }
        }

        // Optimstic update
        commit(
          'SET_LIST',
          state.list.map((creative) => {
            if (toggledCreatives.includes(creative.id)) {
              return { ...creative, status }
            }

            return creative
          })
        )

        return
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async export(
      { commit, rootState },
      {
        pagenumber = 1,
        pagesize = 25,
        orderby = 'id',
        orderdirection = 'desc',
        search = '',
        status = [],
        statusAi = [],
        advertiserdomain = undefined,
      } = {}
    ) {
      try {
        const { organizationId } = rootState.auth.user

        const params = new URLSearchParams()

        params.append('pagenumber', pagenumber)
        params.append('pagesize', pagesize)
        params.append('orderby', orderby)
        params.append('orderdirection', orderdirection)

        if (search) {
          params.append('search', search)
        }
        if (advertiserdomain) {
          params.append('advertiserdomain', advertiserdomain)
        }
        if (status.length > 0) {
          status.forEach((s) => {
            params.append('status', s)
          })
        }
        if (statusAi.length > 0) {
          statusAi.forEach((s) => {
            params.append('statusAi', s)
          })
        }

        return HTTP.get(`Creative/CreativeLibrary/${organizationId}/export/`, {
          params,
          responseType: 'arraybuffer',
          transformResponse: (r) => r,
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async list(
      { commit, rootState },
      {
        pagenumber = 1,
        pagesize = 25,
        orderby = 'id',
        orderdirection = 'desc',
        search = '',
        status = [],
        statusAi = [],
        advertiserdomain = undefined,
      } = {}
    ) {
      try {
        const { organizationId } = rootState.auth.user

        const params = new URLSearchParams()

        params.append('pagenumber', pagenumber)
        params.append('pagesize', pagesize)
        params.append('orderby', orderby)
        params.append('orderdirection', orderdirection)

        if (search) {
          params.append('search', search)
        }
        if (advertiserdomain) {
          params.append('advertiserdomain', advertiserdomain)
        }
        if (status.length > 0) {
          status.forEach((s) => {
            params.append('status', s)
          })
        }
        if (statusAi.length > 0) {
          statusAi.forEach((s) => {
            params.append('statusAi', s)
          })
        }

        const response = await HTTP.get(
          `Creative/CreativeLibrary/${organizationId}/curation/`,
          {
            params,
          }
        )
        const listCreatives = response.data.datas.items

        commit('SET_LIST', listCreatives)
        commit('SET_LIST_TOTAL_PAGES', response.data.datas.totalPages)
        commit('SET_HAS_PREVIOUS', response.data.datas.hasPrevious ?? false)
        commit('SET_HAS_NEXT', response.data.datas.hasNext ?? false)

        return Promise.resolve(response)
      } catch (error) {
        commit('SET_LIST', [])
      }
    },

    async listAdvertisers({ commit, rootState }) {
      try {
        const { organizationId } = rootState.auth.user

        const response = await HTTP.get(
          `Creative/CreativeLibrary/${organizationId}/curation-advertisers/`
        )
        const listAdvertisers = response.data.datas

        commit('SET_LIST_ADVERTISERS', listAdvertisers)

        return Promise.resolve(response)
      } catch (error) {
        commit('SET_LIST_ADVERTISERS', [])
      }
    },

    async fetchBlocklist({ commit, rootState }) {
      try {
        const { organizationId } = rootState.auth.user

        const response = await HTTP.get(
          `Creative/CreativeLibrary/${organizationId}/curation/blocklist`
        )

        commit('SET_BLOCKLIST', response.data.datas ?? [])

        return Promise.resolve(response)
      } catch (error) {
        commit('SET_BLOCKLIST', [])
      }
    },

    async fetchAllowlist({ commit, rootState }) {
      try {
        const { organizationId } = rootState.auth.user

        const response = await HTTP.get(
          `Creative/CreativeLibrary/${organizationId}/curation/allowlist`
        )

        commit('SET_ALLOWLIST', response.data.datas ?? [])

        return Promise.resolve(response)
      } catch (error) {
        commit('SET_ALLOWLIST', [])
      }
    },

    async updateBlocklist({ rootState }, blocklist = []) {
      try {
        const { organizationId } = rootState.auth.user

        const response = await HTTP.put(
          `Creative/CreativeLibrary/${organizationId}/curation/blocklist`,
          {
            wordlist: blocklist,
          }
        )

        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async updateAllowlist({ rootState }, allowlist = []) {
      try {
        const { organizationId } = rootState.auth.user

        const response = await HTTP.put(
          `Creative/CreativeLibrary/${organizationId}/curation/allowlist`,
          {
            wordlist: allowlist,
          }
        )

        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    },
  },
}
